import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import Modal from "./Modal";

/**
 * A confirm dialog that requires the user to type an expected value
 * (usually an email) exactly before the confirm button becomes active.
 * Useful for destructive actions like delete / deactivate.
 *
 * Optional `extra` prop renders additional form fields below the typed
 * input — e.g. a reason textarea for deactivation. `extraValid` lets
 * the caller signal whether those extra fields are valid before enabling
 * the confirm button.
 */
export default function TypedConfirmDialog({
  open,
  title,
  description,
  expectedText,
  inputLabel,
  inputPlaceholder,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "default",
  loading = false,
  onConfirm,
  onClose,
  extra = null,
  extraValid = true,
}) {
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (!open) setTyped("");
  }, [open]);

  const matches = typed.trim() === (expectedText || "").trim();
  const canConfirm = matches && extraValid && !loading;

  const confirmCls =
    tone === "danger"
      ? "inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
      : "btn-primary";

  return (
    <Modal open={open} onClose={loading ? undefined : onClose} title={title} description={description}>
      <div className="space-y-4">
        {extra && <div>{extra}</div>}
        <div>
          <label className="label-base">
            {inputLabel || (
              <>
                Type <code className="rounded bg-gray-100 px-1 py-0.5 text-xs text-gray-800 dark:bg-gray-900 dark:text-gray-200">{expectedText}</code> to confirm
              </>
            )}
          </label>
          <input
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={inputPlaceholder || expectedText}
            autoComplete="off"
            className="input-base"
            disabled={loading}
          />
          {typed && !matches && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              Value does not match.
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </button>
          <button className={confirmCls} onClick={onConfirm} disabled={!canConfirm}>
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}
